import { LoadingOutlined, SearchOutlined } from '@ant-design/icons';
import { Col, Form, notification, Row } from 'antd';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { searchProdutos } from '../../../../../helpers/produtos';
import { useAuth } from '../../../../../security/authContext';
import apiATS from '../../../../../services/apiATS';
import apiNetCore from '../../../../../services/apiNetCore';
import {
  DEFAULT_DATE_FORMAT,
  RANGER_DATE_LIMIT,
  RANGER_DEFAULT_DATE,
  SEARCH_CIDADES_WATCH,
  SEARCH_PRODUTOS_WATCH,
} from '../../../constants';
import {
  CleanButton,
  ConfirmButton,
  FiltrosLote,
  FormButtons,
  FormGroup,
  Label,
  RangerPicker,
  Select,
  Styles,
} from './styles';

const ViagemFilter = ({ onConfirm, onReset }) => {
  const { user: userInfo } = useAuth();
  const { days, unity } = RANGER_DEFAULT_DATE;

  const [cidadesOrigem, setCidadesOrigem] = useState([]);
  const [cidadesDestino, setCidadesDestino] = useState([]);
  const [ibgeOrigem, setIbgeOrigem] = useState([]);
  const [ibgeDestino, setIbgeDestino] = useState([]);
  const [produtos, setProdutos] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [inicioPeriodo, setInicioPeriodo] = useState(
    moment().subtract(days, unity)
  );
  const [finalPeriodo, setFinalPeriodo] = useState(moment());
  const [filterProducts, setFilterProducts] = useState([]);
  const [loadingCidadeOrigem, setLoadingCidadeOrigem] = useState(false);
  const [loadingCidadeDestino, setLoadingCidadeDestino] = useState(false);
  const [loadingSearchProdutos, setLoadingSearchProdutos] = useState(false);

  const [form] = Form.useForm();

  const searchCidadesOrigem = useDebouncedCallback(
    async (value, setLoadingCidade) => {
      if (!value || value.length < 3) {
        if (ibgeOrigem.length >= 1) setIbgeOrigem([]);
        return;
      }

      setCidadesOrigem([]);

      try {
        setLoadingCidade(true);
        const response = await apiATS.get(
          `/CidadeAts/GetCidadeCotacao?cidadeNome=${value}`
        );
        setCidadesOrigem(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        notification.error({
          message: 'Ops, não conseguimos buscar as cidades.',
          description:
            'Caso o problema persista, entre em contato com nosso suporte.',
        });
      } finally {
        setLoadingCidade(false);
      }
    }
  );

  const searchCidadesDestino = useDebouncedCallback(
    async (value, setLoadCidadeDestino) => {
      if (!value || value.length < 3) {
        if (ibgeDestino.length >= 1) setIbgeDestino([]);
        return;
      }

      setCidadesDestino([]);

      try {
        setLoadCidadeDestino(true);
        const response = await apiATS.get(
          `/CidadeAts/GetCidadeCotacao?cidadeNome=${value}`
        );
        setCidadesDestino(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        notification.error({
          message: 'Ops, não conseguimos buscar as cidades.',
          description:
            'Caso o problema persista, entre em contato com nosso suporte.',
        });
      } finally {
        setLoadCidadeDestino(false);
      }
    },
    SEARCH_CIDADES_WATCH
  );

  const onSearchProdutos = useDebouncedCallback(async value => {
    searchProdutos(value, setProdutos, setLoadingSearchProdutos);
  }, SEARCH_PRODUTOS_WATCH);

  // set the period ranger date
  const handlePeriodo = ranger => {
    const [dataInicial, dataFinal] = ranger;

    setInicioPeriodo(dataInicial);
    setFinalPeriodo(dataFinal);
  };

  // handle set the lotes on filter dto
  const handleLotes = lotesArr => {
    setLotes(lotesArr);
  };

  // handle set the "cidades de origem" on filter dto
  const handleCidadeOrigem = (cidade = null) => {
    if (!cidade) {
      setIbgeOrigem([]);
      return;
    }
    setIbgeOrigem([...ibgeOrigem, cidade]);
  };

  // handle set the "cidades de destino" on filter dto
  const handleCidadeDestino = (cidade = null) => {
    if (!cidade) {
      setIbgeDestino([]);
      return;
    }
    setIbgeDestino([...ibgeDestino, cidade]);
  };

  // handle set the "produtos" on filter dto
  const handleProdutos = produto => {
    setFilterProducts(produto);
  };

  // handle reset all form fields
  const handleResetFields = () => {
    setInicioPeriodo(moment().subtract(days, unity));
    setFinalPeriodo(moment());
    setLotes([]);
    setCidadesOrigem([]);
    setIbgeDestino([]);
    setFilterProducts([]);

    form.resetFields();
    onReset();
  };

  // handle the confirm button event
  const handleConfirm = () => {
    const { limit, unitOfTime } = RANGER_DATE_LIMIT;
    const monthsDiff = finalPeriodo.diff(inicioPeriodo, unitOfTime);

    if (monthsDiff >= limit) {
      notification.error({
        message: 'Ops, algo deu errado.',
        description: `O período não pode ser maior que ${limit} meses.`,
      });
      return;
    }

    const confirmFilter = {};

    if (ibgeOrigem.length > 0) confirmFilter.ibge_cidade_origem = ibgeOrigem;
    if (ibgeDestino.length > 0) confirmFilter.ibge_cidade_destino = ibgeDestino;
    if (filterProducts.length > 0) confirmFilter.produtos = filterProducts;
    if (lotes.length > 0) confirmFilter.lotes = lotes;

    if (inicioPeriodo && finalPeriodo) {
      confirmFilter.inicio_periodo = inicioPeriodo.unix();
      confirmFilter.final_periodo = finalPeriodo.unix();
    }

    onConfirm(confirmFilter);
  };

  return (
    <Form form={form}>
      <Row gutter={[8, 4]}>
        <Styles />

        <Col span={24}>
          <Row gutter={[16]}>
            <Col span={5}>
              <FormGroup name="periodo">
                <Label>Período</Label>
                <RangerPicker
                  aria-label="periodoFilter"
                  onChange={handlePeriodo}
                  format={DEFAULT_DATE_FORMAT}
                  defaultValue={[inicioPeriodo, finalPeriodo]}
                />
              </FormGroup>
            </Col>

            <Col span={5}>
              <FormGroup name="lote">
                <Label>ID Lote</Label>
                <FiltrosLote
                  apiNetCore={apiNetCore}
                  clientesPagadores={userInfo.clientes_pagadores}
                  showStatus={true}
                  onChangeCallback={handleLotes}
                  statusLote={[1, 2, 3, 4]}
                  placeholder="Digite o ID do lote"
                />
              </FormGroup>
            </Col>

            <Col span={4}>
              <FormGroup name="origem">
                <Label>Origem</Label>
                <Select
                  allowClear
                  showSearch
                  suffixIcon={<SearchOutlined />}
                  filterOption={false}
                  placeholder="Digite a cidade"
                  notFoundContent={
                    loadingCidadeOrigem ? <LoadingOutlined /> : null
                  }
                  onSearch={value =>
                    searchCidadesOrigem(value, setLoadingCidadeOrigem)
                  }
                  loading={loadingCidadeOrigem}
                  onChange={handleCidadeOrigem}
                  aria-label="filtroCidadeOrigem"
                >
                  {cidadesOrigem.map(cidade => (
                    <Select.Option key={cidade.idCidade} value={cidade.ibge}>
                      {`${cidade.sigla.toUpperCase()} - ${cidade.cidadeNome.toUpperCase()}`}
                    </Select.Option>
                  ))}
                </Select>
              </FormGroup>
            </Col>

            <Col span={4}>
              <FormGroup name="destino">
                <Label>Destino</Label>
                <Select
                  className="filtrosVariacaoFrete"
                  allowClear
                  showSearch
                  suffixIcon={<SearchOutlined />}
                  filterOption={false}
                  placeholder="Digite a Cidade"
                  notFoundContent={
                    loadingCidadeDestino ? <LoadingOutlined /> : null
                  }
                  onSearch={value =>
                    searchCidadesDestino(value, setLoadingCidadeDestino)
                  }
                  onChange={handleCidadeDestino}
                  loading={loadingCidadeDestino}
                  aria-label="filtroCidadeDestino"
                >
                  {cidadesDestino.map(cidade => (
                    <Select.Option key={cidade.idCidade} value={cidade.ibge}>
                      {`${cidade.sigla.toUpperCase()} - ${cidade.cidadeNome.toUpperCase()}`}
                    </Select.Option>
                  ))}
                </Select>
              </FormGroup>
            </Col>

            <Col span={6}>
              <FormGroup name="produto">
                <Label>Produto</Label>
                <Select
                  allowClear
                  showSearch
                  showArrow
                  mode="multiple"
                  suffixIcon={<SearchOutlined />}
                  filterOption={false}
                  placeholder="Digite o nome do produto"
                  notFoundContent={
                    loadingSearchProdutos ? <LoadingOutlined /> : null
                  }
                  onSearch={value => onSearchProdutos(value)}
                  onChange={handleProdutos}
                  loading={loadingSearchProdutos}
                  aria-label="filtroProduto"
                >
                  {produtos.map(produto => (
                    <Select.Option key={produto.id} value={produto.nome}>
                      {produto.nome}
                    </Select.Option>
                  ))}
                </Select>
              </FormGroup>
            </Col>
          </Row>

          <FormButtons gutter={16} justify="end">
            <Col>
              <CleanButton
                aria-label="limparFiltro"
                type="primary"
                onClick={handleResetFields}
              >
                Limpar Filtros
              </CleanButton>
            </Col>

            <Col>
              <ConfirmButton onClick={handleConfirm}>Consultar</ConfirmButton>
            </Col>
          </FormButtons>
        </Col>
      </Row>
    </Form>
  );
};

ViagemFilter.propTypes = {
  onConfirm: PropTypes.func,
  onReset: PropTypes.func,
};

ViagemFilter.defaultProps = {
  onConfirm: () => {},
  onReset: () => {},
};

export default ViagemFilter;
